import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IconCheck, IconClock, IconSparkles, IconX } from "@tabler/icons-react";
import { datadogRum } from "@datadog/browser-rum";
import moment from "moment/moment";
import Card from "../../UI/Card";
import Button from "../../UI/Button";
import IconButton from "../../UI/IconButton";
import Text from "../../UI/Text";
import Title from "../../UI/Title";
import Status from "../../UI/Status";
import StepBadge from "../../UI/StepBadge";
import ProgressBar from "../../UI/ProgressBar";
import {
  STEP_DESC_COLORS,
  STEP_TITLE_COLORS,
  type StepStatus,
} from "../../UI/stepStatus";
import Callout from "../../UI/Callout";
import ModalDialog, { ModalDialogFooter } from "../../UI/ModalDialog";
import Tooltip from "../../UI/Tooltip";
import { useToast } from "../../UI/Toast/useToast";
// @ts-expect-error pure js
import { handleAPIError } from "../../../utils";
import useOnboarding from "../../../hooks/use-onboarding";
// @ts-expect-error pure js
import useBanks from "../../../hooks/use-banks";
// @ts-expect-error pure js
import useAutoActionsAssociations from "../../../hooks/use-auto-actions-associations";
// @ts-expect-error pure js
import useFiles from "../../../hooks/use-files";
// @ts-expect-error pure js
import useCustomization from "../../../hooks/use-customization";
// @ts-expect-error pure js
import useAccount from "../../../hooks/use-account";
// @ts-expect-error pure js
import { TenantStatus } from "../../../models/tenant_old.js";
// @ts-expect-error pure js
import { TypeAbonnement } from "../../../utils/constants/account.js";
// @ts-expect-error pure js
import { currencyFormatter } from "../../../api/Functions.js";
// @ts-expect-error pure js
import { classnames } from "../../../utils";
// @ts-expect-error pure js
import { RumActions } from "../../../utils/constants/rum_actions.js";
import {
  CHECKLIST_1_MISSIONS,
  getChecklist2Missions,
  type MissionContext,
  type MissionDef,
} from "./missions";
import type { Tenant } from "../../../models/tenant";
import type { Property } from "../../../models/property";
import type { ProfileBailleur } from "../../Profile/types";
import styles from "./ActivationChecklist.module.css";

const CHECKLIST_1 = "activation";
const CHECKLIST_2 = "aller-plus-loin";
const AHA_QUITTANCE_GUIDE = "aha-quittance";

interface ActivationChecklistProps {
  tenants: Tenant[];
  properties: Property[];
  profileBailleurs: ProfileBailleur[];
}

interface AutoActionAssociationLike {
  nom_auto_action: string;
}

export default function ActivationChecklist({
  tenants,
  properties,
  profileBailleurs,
}: ActivationChecklistProps) {
  const nav = useNavigate();
  const toast = useToast();
  const { account } = useAccount();
  const {
    onboarding,
    hasSeenGuide,
    markGuideSeen,
    dismissMission,
    completeMission,
    setChecklistCollapsed,
  } = useOnboarding();
  const { banks } = useBanks();
  const { autoActionsAssociations } = useAutoActionsAssociations();
  const { customization } = useCustomization();

  const activeTenants = tenants.filter(
    (tenant) => tenant.status !== TenantStatus.Candidat.status,
  );
  const nbCandidats = tenants.length - activeTenants.length;
  const firstTenant = activeTenants[0];
  const { files } = useFiles(
    firstTenant
      ? { tenantId: firstTenant.id }
      : { propertyId: properties[0]?.id },
  );

  if (!onboarding.segment) return null;

  const allAssociations = (
    Object.values(
      autoActionsAssociations ?? {},
    ) as AutoActionAssociationLike[][]
  ).flat();

  const ctx: MissionContext = {
    hasTenant: activeTenants.length > 0,
    nbCandidats,
    hasQuittanceAuto: allAssociations.some(
      (association) => association.nom_auto_action === "quittance",
    ),
    hasFiles: (files?.length ?? 0) > 0,
    hasBank: (banks?.length ?? 0) > 0,
    hasCustomization: !!(
      customization?.logo_file ||
      customization?.signature_file ||
      customization?.smtp?.serveur
    ),
    hasAbonnement: ![TypeAbonnement.AUCUN, TypeAbonnement.TRIAL].includes(
      account?.type_abonnement,
    ),
    firstTenantId: firstTenant?.id,
    firstPropertyId: properties[0]?.id,
    firstProfileId: profileBailleurs[0]?.id,
  };

  const isDone = (mission: MissionDef) =>
    mission.isDone(ctx) || onboarding.completed_missions.includes(mission.id);
  const notDismissed = (mission: MissionDef) =>
    !onboarding.dismissed_missions.includes(mission.id);

  // La checklist 2 prend la place de la 1 quand toutes les missions restantes
  // de la 1 sont faites (ou retirées), pas dès qu'un locataire existe : en
  // segment « déjà loué », le locataire arrive avant les autres missions.
  const checklist1Missions =
    CHECKLIST_1_MISSIONS[onboarding.segment].filter(notDismissed);
  const checklist2Active = checklist1Missions.every(isDone);

  const checklistId = checklist2Active ? CHECKLIST_2 : CHECKLIST_1;
  const missions = checklist2Active
    ? getChecklist2Missions(ctx.hasAbonnement)
    : CHECKLIST_1_MISSIONS[onboarding.segment];

  const visibleMissions = missions.filter(notDismissed);
  const nbDone = visibleMissions.filter(isDone).length;
  const remaining = visibleMissions.length - nbDone;
  const allDone = remaining === 0;
  const firstTodoIndex = visibleMissions.findIndex(
    (mission) => !isDone(mission),
  );

  // Checklist 1 : les 3 étapes du stepper comptent pour 40 %, les missions
  // pour les 60 % restants. Checklist 2 : progression propre 0-100.
  const percent = checklist2Active
    ? visibleMissions.length === 0
      ? 100
      : Math.round((nbDone / visibleMissions.length) * 100)
    : 40 +
      (visibleMissions.length === 0
        ? 60
        : Math.round((nbDone / visibleMissions.length) * 60));

  const collapsed = onboarding.collapsed_checklists.includes(checklistId);

  // Tout est fait (ou retiré) sur la dernière checklist : l'onboarding est
  // terminé, le widget n'a plus rien à proposer et disparaît définitivement.
  if (checklist2Active && allDone) {
    return null;
  }

  const showAha =
    ctx.hasQuittanceAuto &&
    !hasSeenGuide(AHA_QUITTANCE_GUIDE) &&
    CHECKLIST_1_MISSIONS[onboarding.segment].some((mission) => mission.aha);

  async function missionClickHandler(mission: MissionDef) {
    datadogRum.addAction(RumActions.ONBOARDING_MISSION_CTA, {
      mission: mission.id,
      checklist: checklistId,
    });
    if (mission.manualComplete) {
      try {
        await completeMission(mission.id);
      } catch (error) {
        toast.error({
          title: "Impossible de marquer la mission comme faite",
          description: handleAPIError(error),
        });
      }
    }
    nav(mission.getPath(ctx));
  }

  async function dismissMissionHandler(mission: MissionDef) {
    datadogRum.addAction(RumActions.ONBOARDING_MISSION_DISMISSED, {
      mission: mission.id,
      checklist: checklistId,
    });
    try {
      await dismissMission(mission.id);
    } catch (error) {
      toast.error({
        title: "Impossible de retirer la mission",
        description: handleAPIError(error),
      });
    }
  }

  async function collapseHandler(collapse: boolean) {
    datadogRum.addAction(RumActions.ONBOARDING_CHECKLIST_COLLAPSED, {
      checklist: checklistId,
      collapsed: collapse,
    });
    try {
      await setChecklistCollapsed(checklistId, collapse);
    } catch (error) {
      toast.error({
        title: "Impossible d'enregistrer l'affichage de la checklist",
        description: handleAPIError(error),
      });
    }
  }

  async function markAhaSeenHandler() {
    try {
      await markGuideSeen(AHA_QUITTANCE_GUIDE);
    } catch (error) {
      toast.error({
        title: "Impossible d'enregistrer la fermeture du guide",
        description: handleAPIError(error),
      });
    }
  }

  const title = checklist2Active
    ? allDone
      ? "Votre espace est entièrement configuré ✓"
      : "Allez plus loin avec Qalimo 🚀"
    : allDone
    ? "Votre espace travaille pour vous ✓"
    : "Mettez Qalimo au travail 💪";

  const subtitle = checklist2Active
    ? allDone
      ? "Synchronisation et personnalisation actives. Qalimo gère, vous pilotez."
      : `Votre location tourne toute seule. Encore ${remaining} réglage${
          remaining > 1 ? "s" : ""
        } pour finaliser votre espace.`
    : allDone
    ? "Toutes les automatisations sont actives. Quittances, suivi des loyers et relances tournent sans vous."
    : `Votre espace est configuré. Encore ${remaining} action${
        remaining > 1 ? "s" : ""
      } et Qalimo gère votre location à votre place.`;

  return (
    <div className={styles.wrapper}>
      {checklist2Active && (
        <Callout variant="success" title="Votre espace est configuré.">
          Locataire ajouté, votre location est suivie dans Qalimo. Quelques
          réglages permettent d'aller encore plus loin.
        </Callout>
      )}

      {collapsed ? (
        <Card className={styles.collapsedCard}>
          <Text weight="bold">{title}</Text>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => collapseHandler(false)}
          >
            Reprendre la configuration
          </Button>
        </Card>
      ) : (
        <Card className={styles.card}>
          <div className={styles.header}>
            <div>
              <Title variant="kilo">{title}</Title>
              <Text color="muted">{subtitle}</Text>
            </div>
            <Button
              type="button"
              variant="tertiary"
              onClick={() => collapseHandler(true)}
            >
              Masquer
            </Button>
          </div>

          <div className={styles.progressLine}>
            <ProgressBar value={percent} label="Progression de la checklist" />
            <Text as="span" variant="deci" weight="bold" color="primary">
              {percent}&nbsp;%
            </Text>
          </div>

          <div className={styles.missions}>
            {visibleMissions.map((mission, index) => {
              const done = isDone(mission);
              const status: StepStatus = done
                ? "done"
                : firstTodoIndex === index
                ? "active"
                : "pending";
              return (
                <div
                  key={mission.id}
                  className={classnames(styles.mission, styles[status])}
                >
                  <StepBadge status={status} number={index + 1} />
                  <div className={styles.missionBody}>
                    <div className={styles.missionTitle}>
                      <Text
                        as="span"
                        weight="bold"
                        color={STEP_TITLE_COLORS[status]}
                      >
                        {mission.title}
                      </Text>
                      {!done && mission.facultatif && (
                        <Status as="span" color="neutral" text="Facultatif" />
                      )}
                    </div>
                    <Text color={STEP_DESC_COLORS[status]}>
                      {done ? mission.doneDesc : mission.desc}
                    </Text>
                  </div>
                  {done ? (
                    <Status
                      color="success"
                      weight="bold"
                      icon={IconCheck}
                      text={mission.donePill ?? "Fait"}
                    />
                  ) : (
                    <>
                      <Text
                        as="span"
                        color="muted"
                        className={styles.missionTime}
                      >
                        <IconClock size={14} />
                        {mission.time}
                      </Text>
                      <Button
                        type="button"
                        variant={status === "active" ? "primary" : "secondary"}
                        size="sm"
                        onClick={() => missionClickHandler(mission)}
                        className={styles.missionCTA}
                      >
                        {mission.cta}
                      </Button>
                      <Tooltip content="Je n'en ai pas besoin, retirer cette mission">
                        <IconButton
                          icon={IconX}
                          label="Retirer cette mission"
                          variant="plain"
                          size="sm"
                          onClick={() => dismissMissionHandler(mission)}
                        />
                      </Tooltip>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <Callout variant="warning" icon={IconSparkles}>
            {checklist2Active
              ? "La synchronisation bancaire est en lecture seule et désactivable à tout moment : vos identifiants ne sont jamais stockés par Qalimo."
              : "Chaque action activée = du temps gagné tous les mois sur la gestion de votre location."}
          </Callout>
        </Card>
      )}

      <AhaQuittanceModal
        open={showAha}
        onSeen={markAhaSeenHandler}
        tenant={firstTenant}
      />
    </div>
  );
}

interface AhaQuittanceModalProps {
  open: boolean;
  onSeen: () => void;
  tenant?: Tenant;
}

function AhaQuittanceModal({ open, onSeen, tenant }: AhaQuittanceModalProps) {
  useEffect(() => {
    if (open) {
      datadogRum.addAction(RumActions.ONBOARDING_AHA_QUITTANCE_SHOWN);
    }
  }, [open]);

  return (
    <ModalDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onSeen();
      }}
      size="sm"
      header="Vos quittances sont automatisées !"
      subtitle="Voici la quittance qui partira sans aucune action de votre part :"
    >
      <Card padding="sm" className={styles.quittance}>
        <div className={styles.quittanceHead}>
          <Text weight="bold">
            Quittance de loyer · {moment().add(1, "month").format("MMMM YYYY")}
          </Text>
          <Status color="info" text="Envoi automatique le 1er du mois" />
        </div>
        <div>
          <div className={styles.quittanceRow}>
            <Text as="span" color="muted">
              Locataire
            </Text>
            <Text as="span">{tenant?.nom}</Text>
          </div>
          <div className={styles.quittanceRow}>
            <Text as="span" color="muted">
              Loyer hors charges
            </Text>
            <Text as="span">{currencyFormatter(tenant?.loyer_hc ?? 0)}</Text>
          </div>
          <div className={styles.quittanceRow}>
            <Text as="span" color="muted">
              Charges
            </Text>
            <Text as="span">{currencyFormatter(tenant?.charges ?? 0)}</Text>
          </div>
        </div>
        <div className={classnames(styles.quittanceRow, styles.quittanceTotal)}>
          <Text as="span" weight="bold">
            Total
          </Text>
          <Text as="span" weight="bold">
            {currencyFormatter(
              (tenant?.loyer_hc ?? 0) + (tenant?.charges ?? 0),
            )}
          </Text>
        </div>
      </Card>
      <ModalDialogFooter>
        <Button type="button" variant="primary" onClick={onSeen}>
          Parfait, continuer
        </Button>
      </ModalDialogFooter>
    </ModalDialog>
  );
}
